import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AnimatePresence } from "framer-motion";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import WhatsAppButton from "@/components/WhatsAppButton";
import PageTransition from "@/components/PageTransition";
import { AuthProvider } from "@/hooks/useAuth";
import Index from "./pages/Index.tsx";

const Portfolio = lazy(() => import("./pages/Portfolio.tsx"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail.tsx"));
const About = lazy(() => import("./pages/About.tsx"));
const Contact = lazy(() => import("./pages/Contact.tsx"));
const Services = lazy(() => import("./pages/Services.tsx"));
const Media = lazy(() => import("./pages/Media.tsx"));
const BlogPost = lazy(() => import("./pages/BlogPost.tsx"));
const Calculator = lazy(() => import("./pages/Calculator.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));

const AdminLogin = lazy(() => import("./pages/admin/AdminLogin.tsx"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout.tsx"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard.tsx"));
const PagesList = lazy(() => import("./pages/admin/Pages.tsx").then(m => ({ default: m.PagesList })));
const PageEdit = lazy(() => import("./pages/admin/Pages.tsx").then(m => ({ default: m.PageEdit })));
const ProjectsList = lazy(() => import("./pages/admin/Projects.tsx").then(m => ({ default: m.ProjectsList })));
const ProjectEdit = lazy(() => import("./pages/admin/Projects.tsx").then(m => ({ default: m.ProjectEdit })));
const MediaList = lazy(() => import("./pages/admin/Media.tsx").then(m => ({ default: m.MediaList })));
const MediaEdit = lazy(() => import("./pages/admin/Media.tsx").then(m => ({ default: m.MediaEdit })));
const Seo = lazy(() => import("./pages/admin/Seo.tsx"));
const Users = lazy(() => import("./pages/admin/Users.tsx"));

const queryClient = new QueryClient();

const RouteFallback = () => (
  <div className="min-h-screen bg-background" aria-hidden="true" />
);

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={<RouteFallback />}>
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<PageTransition><Index /></PageTransition>} />
          <Route path="/portfolio" element={<PageTransition><Portfolio /></PageTransition>} />
          <Route path="/portfolio/:slug" element={<PageTransition><ProjectDetail /></PageTransition>} />
          <Route path="/about" element={<PageTransition><About /></PageTransition>} />
          <Route path="/contact" element={<PageTransition><Contact /></PageTransition>} />
          <Route path="/services" element={<PageTransition><Services /></PageTransition>} />
          <Route path="/media" element={<PageTransition><Media /></PageTransition>} />
          <Route path="/media/:slug" element={<PageTransition><BlogPost /></PageTransition>} />
          <Route path="/calculator" element={<PageTransition><Calculator /></PageTransition>} />

          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Dashboard />} />
            <Route path="pages" element={<PagesList />} />
            <Route path="pages/:id" element={<PageEdit />} />
            <Route path="projects" element={<ProjectsList />} />
            <Route path="projects/:id" element={<ProjectEdit />} />
            <Route path="media" element={<MediaList />} />
            <Route path="media/:id" element={<MediaEdit />} />
            <Route path="seo" element={<Seo />} />
            <Route path="users" element={<Users />} />
          </Route>

          <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <AnimatedRoutes />
            <WhatsAppButton />
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
